import React, { useState, useEffect } from "react";
import { VehicleListItem, VehicleSearch } from "../models";
import { Link } from "react-router-dom";
import { Hub, KeyValue } from "common/models";
import orgDataService from "services/orgDataService";
import vehiclesService from "services/vehiclesService";
import { Skeleton, Select, MenuItem, FormControl, FormControlLabel, InputLabel, Box, Paper, Grid, Checkbox } from "@mui/material";
import {
  DataGrid,
  GridCheckIcon,
  GridCloseIcon,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import MDButton from "components/MDButton";
import VehicleUnitNumberSearch from "common/components/search/VehicleUnitNumber";
import MapIcon from "@mui/icons-material/Map";

const VehiclesList = () => {
  let persistedSearch = localStorage.getItem("vehicles_list");
  let initialSearch: VehicleSearch = {
    pagedSearchOptions: { offset: 0, pageSize: 25 },
    sortAsc: true,
    sortName: "id",
  };
  const [search, setSearch] = useState<VehicleSearch>( persistedSearch ? JSON.parse(persistedSearch) : initialSearch);
  const [vehicles, setVehicles] = useState<VehicleListItem[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [types, setTypes] = useState<KeyValue[]>();
  const [statuses, setStatuses] = useState<KeyValue[]>();
  const gridRef = useGridApiRef();

  const columns: GridColDef[] = [
    {
      field: "unitNumber",
      headerName: "Unit Number",
      flex: 1,
      renderCell: (params: GridRenderCellParams<any, number>) => (
        <Link to={`${params.value}?id=${params.row.id}`} state={{ id: params.row.id }}>
          {params.value}
        </Link>
      ),
    },
    { field: "status", headerName: "Status", flex:1 },
    {
      field: " ",
      headerName: "Current Location",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, number>) => (
        <>
          {!params.row.description.match(/(rental)|(test)|(trailer)/gi) &&
            !params.row.hub.match(/(junk)|(barge)/gi) && (
              <Link title="View History" to={`/maps/current-location?unitnumber=${params.row.unitNumber}`}>
                <MapIcon />
              </Link>
            )}
        </>
      ),
    },
    {
      field: "latestTripId",
      headerName: "Current Trip",
      sortable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams<any, number>) => (
        <Link to={`/trips/${params.row.latestTripId}`}>{params.value}</Link>
      ),
    },
    { field: "hub", headerName: "Hub", flex: 1 },
    { field: "description", headerName: "Description", flex: 1 },
    { field: "type", headerName: "Type", width: 110 },
    {
      field: "isActive",
      headerName: "Is Active",
      width: 90,
      sortable: false,
      align: "center",
      renderCell: (params: GridRenderCellParams<any, boolean>) => params.value ? <GridCheckIcon style={{fill: "green"}}/>: <GridCloseIcon style={{fill: "red", textAlign:"center"}}
      />,
    },
  ];

  useEffect(() => {
    orgDataService.getAllHubs().then((data) => setHubs(data));
    orgDataService.getVehicleTypes().then((data) => setTypes(data));
    orgDataService.getVehicleStatuses().then((data) => setStatuses(data));
  }, []);

  useEffect(() => {
    setVehicles([]);
    setTotal(0);
    setIsLoading(true);
    localStorage.setItem('vehicles_list', JSON.stringify(search));
    vehiclesService
      .searchVehicles(search)
      .then((results) => {
        setVehicles(results.results);
        setTotal(results.count);
      })
      .finally(() => setIsLoading(false));
  }, [search]);

  function handleOnChange(e: any) {
    var request = { ...search, [e.target.name]: e.target.value };
    setSearch(request);
    gridRef.current.setPage(0);
  }

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (!(sortModel.length > 0)) return;
    var request = {
      ...search,
      sortName: sortModel[0].field,
      sortAsc: sortModel[0].sort == "asc",
      pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
    };
    setSearch(request);
    gridRef.current.setPage(0);
  };

  const handlePaginationModel = (pagination: GridPaginationModel) => {
    if (pagination.pageSize != search.pagedSearchOptions.pageSize) {
      pagination.page = 0;
    }
    setSearch({ ...search, pagedSearchOptions: { pageSize: pagination.pageSize, offset: pagination.page } });
  };

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(search.pagedSearchOptions.pageSize)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" width="98%" sx={{ my: 3, mx: 1, overflow: "hidden" }} />
      ))}
    </Box>
  );

  const onUnitNumberChange = (value) => {
    setSearch({ ...search, unitNumber: value?.name });
    gridRef.current.setPage(0);
  };

  const clearFilters = ()=>{
    setSearch({...initialSearch, hubId: undefined, typeId: undefined, statusId: undefined, unitNumber: null});
  }
  return (
    <>
      <Box display={"flex"} justifyContent={"space-between"}>
        <Grid container>
          <FormControl sx={{ mt: 2, ml: 1, minWidth: 120 }}>
            <InputLabel id="hubId-label">Hub</InputLabel>
            <Select
              id="hubId"
              name="hubId"
              labelId="hubId-label"
              label="Hub"
              value={search.hubId || ""}
              onChange={handleOnChange}
            >
              <MenuItem key={0} value=" ">
                {"All"}
              </MenuItem>
              {hubs &&
                hubs.map((h, index) => {
                  return (
                    <MenuItem key={index} value={h.id}>
                      {h.name}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
          <FormControl sx={{ mt: 2, ml: 2, minWidth: 120 }}>
            <InputLabel id="typeId-label">Type</InputLabel>
            <Select
              id="typeId"
              name="typeId"
              labelId="typeId-label"
              label="Type"
              sx={{ minWidth: 120 }}
              value={search.typeId || ""}
              onChange={handleOnChange}
            >
              <MenuItem key={0} value=" ">
                {"All"}
              </MenuItem>
              {types &&
                types.map((t, index) => {
                  return (
                    <MenuItem key={index} value={t.id}>
                      {t.name}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
          <FormControl sx={{ mt: 2, ml: 2, minWidth: 120 }}>
            <InputLabel id="statusId-label">Status</InputLabel>
            <Select
              id="statusId"
              name="statusId"
              labelId="statusId-label"
              label="Status"
              sx={{ minWidth: 120 }}
              value={search.statusId || ""}
              onChange={handleOnChange}
              
            >
              <MenuItem key={0} value=" ">
                {"All"}
              </MenuItem>
              {statuses &&
                statuses.map((s, index) => {
                  return (
                    <MenuItem key={index} value={s.id}>
                      {s.name}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
          <VehicleUnitNumberSearch marginLeft={2} onChange={onUnitNumberChange} 
          unitNumber={search.unitNumber ? { name: `${search.unitNumber}`, id: +search.unitNumber } : {name:'', value:null}}/>
          <FormControlLabel
            sx={{ mt: 2, ml: 1 }}
            label="Include inactive"
            control={
              <Checkbox
                checked={!!search.includeInactive}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    includeInactive: e.target.checked || undefined,
                    pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 },
                  });
                  gridRef.current.setPage(0);
                }}
              />
            }
          />
          <MDButton sx={{mt:2, ml:1}} onClick={clearFilters}>Clear</MDButton>
        </Grid>
        <MDButton
          sx={{ mr: 2, alignSelf: "center" }}
          variant="contained"
          color="dark"
          to="/vehicles/create"
          component={Link}
        >
          Add
        </MDButton>
      </Box>
      <Box sx={{ mt: 2, overflow: "hidden" }}>
        <Paper sx={{ width: "99%", height: isLoading ? "100vh" : "auto", overflow: "hidden" }}>
          <DataGrid
            sx={{ overflow: "hidden", fontSize: ".95em" }}
            rows={vehicles}
            columns={columns}
            pagination
            sortingMode="server"
            paginationMode="server"
            onPaginationModelChange={handlePaginationModel}
            onSortModelChange={handleSortModelChange}
            initialState={{
              pagination: { paginationModel: search.pagedSearchOptions },
              sorting: { sortModel: [{ field: "lastName", sort: "asc" }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={total}
            disableColumnMenu
            slots={{
              loadingOverlay: LoadingSkeleton,
            }}
            loading={isLoading}
            density="compact"
            apiRef={gridRef as any}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
          />
        </Paper>
      </Box>
    </>
  );
};

export default VehiclesList;
